import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { IoMdClose } from 'react-icons/io';
import { connect } from 'react-redux';
import { emailRegex } from '../../assets/constants/strings';
import { sendQuote, cleanEnquiry } from '../../store/User/userActions';
import '../../components/common/contactModal.scss';

// Contact-us modal for the reader app. Reader paid access is sales-led (no
// self-serve pricing), so this posts an Enquiry (POST /v1/Enquiry) tagged
// Product=ReaderApp — the conversion path to an Enterprise reader account,
// mirroring the API's ContactSalesModal. (Previously a dead stub whose Send
// button only closed the dialog.)
const ContactUsModal = ({
  user,
  enquiry,
  loading,
  show,
  handleClose,
  sendQuote,
  cleanEnquiry,
}) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [organisation, setOrganisation] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  // Pre-fill from the logged-in account.
  useEffect(() => {
    if (user) {
      setFirstName(user.firstName || '');
      setLastName(user.lastName || '');
      setEmail(user.email || '');
      setOrganisation(user.organisation || '');
    }
  }, [user]);

  // Reset transient state each time the modal is opened.
  useEffect(() => {
    if (show) {
      setSent(false);
      setSubmitting(false);
      setMessage('');
      cleanEnquiry();
    }
  }, [show, cleanEnquiry]);

  // React to the dispatched enquiry result.
  useEffect(() => {
    if (submitting && enquiry) {
      setSubmitting(false);
      if (enquiry.success) {
        setSent(true);
        setMessage('');
      }
    }
  }, [enquiry, submitting]);

  const isValid =
    firstName.trim() &&
    lastName.trim() &&
    emailRegex(email) &&
    organisation.trim() &&
    message.trim();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValid || submitting) return;

    setSubmitting(true);
    sendQuote({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      organisation: organisation.trim(),
      phoneNumber: phoneNumber.trim(),
      jobTitle: '',
      userTier: 0,
      product: 'ReaderApp',
      message: `Enquiry about RegulationCity Enterprise access.\n\n${message.trim()}`,
    });
  };

  const onHide = () => {
    cleanEnquiry();
    handleClose();
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName='cu-dialog'
      contentClassName='cu-content'
      backdropClassName='cu-backdrop'
    >
      <div className='cu'>
        <div className='cu__head'>
          <div className='cu__title'>Contact Us</div>
          {!sent && (
            <div className='cu__sub'>
              Tell us about your team and we'll be in touch to scope the right
              Enterprise setup.
            </div>
          )}
          <button
            type='button'
            className='cu__x'
            onClick={onHide}
            aria-label='Close'
          >
            <IoMdClose />
          </button>
        </div>

        {sent ? (
          <>
            <div className='cu__confirm'>
              <div className='cu__badge'>
                <svg viewBox='0 0 24 24' fill='none'>
                  <path
                    d='M5 13l4 4L19 7'
                    stroke='#fff'
                    strokeWidth='2.6'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
              <div className='cu__confirm-title'>
                Thanks — we've got your enquiry
              </div>
              <div className='cu__confirm-text'>
                Our team will be in touch at <b>{email}</b> shortly to discuss
                the right plan for you.
              </div>
            </div>
            <div className='cu__foot'>
              <button type='button' className='cu__btn' onClick={onHide}>
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            <form className='cu__body' autoComplete='off' onSubmit={handleSubmit}>
              <div className='cu__row'>
                <div className='cu__fg'>
                  <label>
                    First name<span className='cu__req'>*</span>
                  </label>
                  <input
                    id='FirstName'
                    value={firstName}
                    maxLength={35}
                    required
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </div>
                <div className='cu__fg'>
                  <label>
                    Last name<span className='cu__req'>*</span>
                  </label>
                  <input
                    id='LastName'
                    value={lastName}
                    maxLength={35}
                    required
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
              </div>
              <div className='cu__row'>
                <div className='cu__fg'>
                  <label>
                    Email<span className='cu__req'>*</span>
                  </label>
                  <input
                    id='Email'
                    type='email'
                    value={email}
                    maxLength={320}
                    required
                    className={
                      email.length && !emailRegex(email) ? 'cu__invalid' : ''
                    }
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div className='cu__fg'>
                  <label>
                    Company<span className='cu__req'>*</span>
                  </label>
                  <input
                    id='CompanyName'
                    type='text'
                    value={organisation}
                    maxLength={100}
                    required
                    onChange={(e) => setOrganisation(e.target.value)}
                  />
                </div>
              </div>
              <div className='cu__fg'>
                <label>
                  Phone <span className='cu__opt'>(optional)</span>
                </label>
                <input
                  id='PhoneNumber'
                  type='tel'
                  value={phoneNumber}
                  maxLength={30}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                />
              </div>
              <div className='cu__fg'>
                <label>
                  Message<span className='cu__req'>*</span>
                </label>
                <textarea
                  placeholder='Tell us about your team and what you need'
                  rows={5}
                  value={message}
                  maxLength={2000}
                  required
                  onChange={(e) => setMessage(e.target.value)}
                />
              </div>
            </form>
            <div className='cu__foot'>
              <button
                type='button'
                className='cu__btn'
                onClick={handleSubmit}
                disabled={!isValid || submitting || loading}
              >
                {submitting ? 'Sending…' : 'Send enquiry'}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  user: state.user.user,
  enquiry: state.user.enquiry,
  loading: state.user.loading,
});

const mapDispatchToProps = { sendQuote, cleanEnquiry };

export default connect(mapStateToProps, mapDispatchToProps)(ContactUsModal);
